import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "upload-filters-panel bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4" }
const _hoisted_2 = ["aria-expanded"]
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "rounded-full bg-gray-800 px-2 py-0.5 text-xs font-bold text-white"
}
const _hoisted_5 = { class: "flex items-center gap-3 text-sm text-gray-600" }
const _hoisted_6 = { class: "flex flex-wrap items-end gap-3" }
const _hoisted_7 = { class: "filter-control flex-1 min-w-[200px]" }
const _hoisted_8 = { class: "filter-control w-32" }
const _hoisted_9 = { class: "filter-control w-40" }
const _hoisted_10 = { class: "filter-control w-48" }
const _hoisted_11 = ["value"]
const _hoisted_12 = { class: "filter-control w-48" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["value"]
const _hoisted_15 = { class: "filter-control w-52" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = ["value"]
const _hoisted_18 = { class: "flex items-center gap-3 pt-1" }
const _hoisted_19 = { class: "flex gap-2" }
const _hoisted_20 = { class: "border-t pt-3 text-sm font-semibold text-gray-700 flex items-center gap-3" }

import { computed, ref, watch } from 'vue'

import type { UploadProductFiltersData, UploadProductFiltersProps } from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'UploadProductFilters',
  props: {
    totalProduits: { default: 0 },
    categories: { default: () => [] },
    sousCategories: { default: () => [] },
    sousSousCategories: { default: () => [] },
    categoriesTree: { default: () => [] },
    initialFilters: { default: () => ({
    reference: '',
    idShooting: '',
    tri: 'date-desc',
    categorie: '',
    sousCategorie: '',
    sousSousCategorie: '',
    hasPhotos: '',
    premelFilter: ''
  }) }
  },
  emits: ["filter", "showAll"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// État des filtres (initialisé à partir des props afin de refléter la persistance)
const filters = ref<UploadProductFiltersData>({ ...props.initialFilters })
const areFiltersOpen = ref(false)

const activeFiltersCount = computed(() => {
  const values = [
    filters.value.reference,
    filters.value.idShooting,
    filters.value.categorie,
    filters.value.sousCategorie,
    filters.value.sousSousCategorie,
    filters.value.hasPhotos,
    filters.value.premelFilter,
  ]
  return values.filter(Boolean).length + (filters.value.tri !== 'date-desc' ? 1 : 0)
})

// Mettre à jour si le parent change l'état initial (ex: restauré depuis Pinia)
watch(() => props.initialFilters, (nf) => {
  if (!nf) return
  filters.value = { ...nf }
}, { deep: true })

// Catégories à 3 niveaux basées sur props.categoriesTree (comme ProductSheetModal)
const categoriesOptions = computed(() => {
  // Si categoriesTree est fourni, l'utiliser
  if (props.categoriesTree && props.categoriesTree.length > 0) {
    return props.categoriesTree.map(cat => ({
      value: String(cat.id_categorie),
      label: cat.label
    }))
  }
  // Fallback sur l'ancien système
  return props.categories
})

const sousCategoriesOptionsFiltered = computed(() => {
  // Si categoriesTree est fourni, naviguer dans l'arbre
  if (props.categoriesTree && props.categoriesTree.length > 0) {
    if (!filters.value.categorie) return []
    const catId = parseInt(filters.value.categorie)
    const found = props.categoriesTree.find(c => c.id_categorie === catId)
    if (!found || !found.children) return []
    return found.children.map(cat => ({
      value: String(cat.id_categorie),
      label: cat.label
    }))
  }
  // Fallback sur l'ancien système
  if (!filters.value.categorie) {
    return props.sousCategories
  }
  return props.sousCategories.filter(option => option.parentId === filters.value.categorie)
})

const sousSousCategoriesOptionsFiltered = computed(() => {
  // Si categoriesTree est fourni, naviguer dans l'arbre
  if (props.categoriesTree && props.categoriesTree.length > 0) {
    if (!filters.value.categorie || !filters.value.sousCategorie) return []
    const catId = parseInt(filters.value.categorie)
    const sousCatId = parseInt(filters.value.sousCategorie)
    const level1 = props.categoriesTree.find(c => c.id_categorie === catId)
    if (!level1 || !level1.children) return []
    const level2 = level1.children.find(c => c.id_categorie === sousCatId)
    if (!level2 || !level2.children) return []
    return level2.children.map(cat => ({
      value: String(cat.id_categorie),
      label: cat.label
    }))
  }
  // Fallback sur l'ancien système
  if (!filters.value.sousCategorie) {
    return props.sousSousCategories
  }
  return props.sousSousCategories.filter(option => option.parentId === filters.value.sousCategorie)
})

const setPremelFilter = (value: '' | 'en_ligne' | 'brouillon') => {
  filters.value.premelFilter = value
}

const handleShowAllWithReset = () => {
  filters.value.premelFilter = ''
  handleSubmit()
  emit('showAll')
}

// Soumettre les filtres
const handleSubmit = () => {
  emit('filter', { ...filters.value })
}

// Réinitialiser les filtres
const resetFilters = () => {
  filters.value = {
    reference: '',
    idShooting: '',
    tri: 'date-desc',
    categorie: '',
    sousCategorie: '',
    sousSousCategorie: '',
    hasPhotos: '',
    premelFilter: ''
  }
  handleSubmit()
}

// Debounce server-side filtering to avoid excessive requests and improve UX (like ProductFilters)
let filterDebounceTimer: number | undefined

// Watch sur référence pour filtrage en temps réel avec debounce
watch(() => filters.value.reference, () => {
  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer)
  filterDebounceTimer = window.setTimeout(() => {
    handleSubmit()
  }, 400)
})

watch(() => filters.value.idShooting, () => {
  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer)
  filterDebounceTimer = window.setTimeout(() => {
    handleSubmit()
  }, 400)
})

watch(() => filters.value.tri, () => {
  handleSubmit()
})

watch(() => filters.value.hasPhotos, () => {
  handleSubmit()
})

watch(() => filters.value.premelFilter, () => {
  handleSubmit()
})

watch(() => filters.value.categorie, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    filters.value.sousCategorie = ''
    filters.value.sousSousCategorie = ''
    handleSubmit()
  }
})

watch(() => filters.value.sousCategorie, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    filters.value.sousSousCategorie = ''
    handleSubmit()
  }
})

watch(() => filters.value.sousSousCategorie, () => {
  handleSubmit()
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      type: "button",
      class: "upload-filters-toggle",
      "aria-expanded": areFiltersOpen.value,
      "aria-controls": "upload-product-filters-form",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (areFiltersOpen.value = !areFiltersOpen.value))
    }, [
      _createElementVNode("span", _hoisted_3, [
        _cache[11] || (_cache[11] = _createElementVNode("i", {
          class: "fas fa-filter text-gray-600",
          "aria-hidden": "true"
        }, null, -1)),
        _cache[12] || (_cache[12] = _createElementVNode("span", { class: "font-semibold text-gray-900" }, "Filtres", -1)),
        (activeFiltersCount.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(activeFiltersCount.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("span", _hoisted_5, [
        _createTextVNode(_toDisplayString(_ctx.totalProduits) + " " + _toDisplayString(_ctx.totalProduits > 1 ? 'produits' : 'produit') + " ", 1),
        _createElementVNode("i", {
          class: _normalizeClass(["fas fa-chevron-down text-xs transition-transform", areFiltersOpen.value ? 'rotate-180' : '']),
          "aria-hidden": "true"
        }, null, 2)
      ])
    ], 8, _hoisted_2),
    _createElementVNode("form", {
      id: "upload-product-filters-form",
      onSubmit: _withModifiers(handleSubmit, ["prevent"]),
      class: _normalizeClass(["upload-filters-form space-y-3", { 'is-open': areFiltersOpen.value }])
    }, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.reference) = $event)),
            type: "text",
            placeholder: "🔍 Rechercher une référence...",
            class: "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          }, null, 512), [
            [
              _vModelText,
              filters.value.reference,
              void 0,
              { trim: true }
            ]
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.idShooting) = $event)),
            type: "text",
            placeholder: "ID Shooting",
            class: "w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          }, null, 512), [
            [
              _vModelText,
              filters.value.idShooting,
              void 0,
              { trim: true }
            ]
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.value.hasPhotos) = $event)),
            class: "w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          }, _cache[13] || (_cache[13] = [
            _createElementVNode("option", { value: "" }, "Toutes", -1),
            _createElementVNode("option", { value: "with" }, "Avec photos", -1),
            _createElementVNode("option", { value: "without" }, "Sans photos", -1)
          ]), 512), [
            [_vModelSelect, filters.value.hasPhotos]
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.categorie) = $event)),
            class: "w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          }, [
            _cache[14] || (_cache[14] = _createElementVNode("option", { value: "" }, "Catégorie", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categoriesOptions.value, (option) => {
              return (_openBlock(), _createElementBlock("option", {
                key: option.value,
                value: option.value
              }, _toDisplayString(option.label), 9, _hoisted_11))
            }), 128))
          ], 512), [
            [_vModelSelect, filters.value.categorie]
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.sousCategorie) = $event)),
            disabled: !filters.value.categorie,
            class: "w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
          }, [
            _cache[15] || (_cache[15] = _createElementVNode("option", { value: "" }, "Sous-catégorie", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sousCategoriesOptionsFiltered.value, (option) => {
              return (_openBlock(), _createElementBlock("option", {
                key: option.value,
                value: option.value
              }, _toDisplayString(option.label), 9, _hoisted_14))
            }), 128))
          ], 8, _hoisted_13), [
            [_vModelSelect, filters.value.sousCategorie]
          ])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.value.sousSousCategorie) = $event)),
            disabled: !filters.value.sousCategorie,
            class: "w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
          }, [
            _cache[16] || (_cache[16] = _createElementVNode("option", { value: "" }, "Sous-sous-catégorie", -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sousSousCategoriesOptionsFiltered.value, (option) => {
              return (_openBlock(), _createElementBlock("option", {
                key: option.value,
                value: option.value
              }, _toDisplayString(option.label), 9, _hoisted_17))
            }), 128))
          ], 8, _hoisted_16), [
            [_vModelSelect, filters.value.sousSousCategorie]
          ])
        ]),
        _createElementVNode("button", {
          type: "button",
          onClick: resetFilters,
          class: "px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50",
          title: "Réinitialiser"
        }, _cache[17] || (_cache[17] = [
          _createElementVNode("i", { class: "fas fa-undo" }, null, -1)
        ]))
      ]),
      _createElementVNode("div", _hoisted_18, [
        _cache[20] || (_cache[20] = _createElementVNode("label", { class: "text-xs font-medium text-gray-700" }, "Trier par", -1)),
        _createElementVNode("div", _hoisted_19, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass([
              'px-3 py-1 text-xs font-medium rounded border transition-colors',
              filters.value.tri === 'date-desc' || filters.value.tri === 'date-asc'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]),
            onClick: _cache[7] || (_cache[7] = ($event: any) => (filters.value.tri = 'date-desc'))
          }, _cache[18] || (_cache[18] = [
            _createElementVNode("i", { class: "fas fa-calendar-alt mr-1" }, null, -1),
            _createTextVNode("Date ")
          ]), 2),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass([
              'px-3 py-1 text-xs font-medium rounded border transition-colors',
              filters.value.tri === 'reference-asc' || filters.value.tri === 'reference-desc'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]),
            onClick: _cache[8] || (_cache[8] = ($event: any) => (filters.value.tri = 'reference-asc'))
          }, _cache[19] || (_cache[19] = [
            _createElementVNode("i", { class: "fas fa-sort-alpha-down mr-1" }, null, -1),
            _createTextVNode("Alphabet ")
          ]), 2)
        ])
      ]),
      _createElementVNode("div", _hoisted_20, [
        _createElementVNode("span", null, _toDisplayString(_ctx.totalProduits) + " " + _toDisplayString(_ctx.totalProduits > 1 ? 'produits' : 'produit'), 1),
        _createElementVNode("button", {
          type: "button",
          onClick: handleShowAllWithReset,
          class: "px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors",
          title: "Voir tous les uploads"
        }, " Voir tout "),
        _createElementVNode("button", {
          type: "button",
          onClick: _cache[9] || (_cache[9] = ($event: any) => (setPremelFilter('en_ligne'))),
          class: _normalizeClass([
            'px-2 py-1 text-xs rounded border transition-colors',
            filters.value.premelFilter === 'en_ligne'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          ]),
          title: "Afficher uniquement les produits en ligne"
        }, " En ligne ", 2),
        _createElementVNode("button", {
          type: "button",
          onClick: _cache[10] || (_cache[10] = ($event: any) => (setPremelFilter('brouillon'))),
          class: _normalizeClass([
            'px-2 py-1 text-xs rounded border transition-colors',
            filters.value.premelFilter === 'brouillon'
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          ]),
          title: "Afficher uniquement les produits en brouillon"
        }, " Brouillon ", 2)
      ])
    ], 34)
  ]))
}
}

})